import { useState } from 'react';
import { ArrowLeft, Save } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from 'urql';
import Swal from 'sweetalert2';
import PageHeaderCompact from '@/components/common/PageHeaderCompact.jsx';
import MetricCardCompact from '@/components/common/MetricCardCompact.jsx';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { CREAR_LINEA_MUTATION } from '@/graphql/lineas.gql.js';
import LineaForm from './LineaForm.jsx';

export default function NuevaLinea() {
  const navigate = useNavigate();
  const { sucursal } = useAuth();
  const [fallbackSucursalId, setFallbackSucursalId] = useState('');
  const sucursalId = Number(sucursal?.id || fallbackSucursalId || 0);
  const [, crearLinea] = useMutation(CREAR_LINEA_MUTATION);

  const handleSubmit = async payload => {
    if (!sucursalId) {
      await Swal.fire('Sucursal requerida', 'Indicá la sucursal para poder crear la línea.', 'warning');
      return;
    }

    const response = await crearLinea({
      input: {
        ...payload,
        sucursal_id: sucursalId,
        localidad_id: null,
      },
    });

    if (response.error) {
      await Swal.fire('No se pudo crear', response.error.message, 'error');
      return;
    }

    await Swal.fire('Línea creada', 'La línea se creó correctamente.', 'success');
    navigate('/lineas');
  };

  return (
    <div className="operator-shell space-y-2.5 px-4 pb-4 pt-2 md:px-5" data-qa-screen="lineas-form">
      <PageHeaderCompact
        title="Nueva línea"
        description="Configuración operativa compacta para emisión de fojas y control de carga."
        breadcrumbs={[
          { label: 'Líneas', href: '/lineas' },
          { label: 'Nueva línea' },
        ]}
        showBreadcrumb
        actions={(
          <Link
            to="/lineas"
            className="inline-flex h-9 items-center gap-2 rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.94)] px-3 text-sm font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--subpanel),0.82)]"
          >
            <ArrowLeft size={14} aria-hidden="true" />
            Volver
          </Link>
        )}
      />

      <div className="grid gap-2.5 lg:grid-cols-[200px_minmax(0,1fr)]">
        <aside className="space-y-2.5">
          <MetricCardCompact
            label="Sucursal"
            value={sucursalId || 'Pendiente'}
            helper={sucursal?.nombre || 'Definí la sucursal activa para habilitar el alta.'}
            tone={sucursalId ? 'brand' : 'warning'}
          />
          <MetricCardCompact
            label="Objetivo"
            value="Validar foja"
            helper="La línea define límites operativos y disponibilidad para el circuito de cargas."
            icon={Save}
          />
          {!sucursalId ? (
            <label className="block rounded-2xl border border-[rgba(var(--border),0.58)] bg-[rgba(var(--panel),0.96)] p-3">
              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[rgba(var(--muted),0.9)]">Sucursal manual</span>
              <input
                value={fallbackSucursalId}
                onChange={e => setFallbackSucursalId(e.target.value)}
                className="mt-2 h-10 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.94)] px-3 text-sm text-[rgb(var(--fg))] outline-none transition focus:ring-2 focus:ring-[rgb(var(--ring))]"
                placeholder="Ingresá sucursalId"
              />
            </label>
          ) : null}
        </aside>

        <LineaForm
          onSubmit={handleSubmit}
          onCancel={() => navigate('/lineas')}
          submitText="Guardar línea"
          initialValues={{ activa: true }}
        />
      </div>
    </div>
  );
}
